import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Shield, Smartphone } from 'lucide-react';
import Logo from '../assets/Logo.png';

const currentYear = new Date().getFullYear();

/* ── Column data ──────────────────────────────────────────────────────────── */

const companyLinks = [
  { name: 'About Us', path: '/about' },
  { name: 'Careers', path: '/careers' },
  { name: 'Press', path: '/press' },
  { name: 'Blog', path: '/blog' },
  { name: 'Partner With Us', path: '/partners' },
];

const customerLinks = [
  { name: 'How It Works', path: '/how-it-works' },
  { name: 'All Services', path: '/services' },
  { name: 'Cost Estimates', path: '/cost-estimates' },
  { name: 'Safety', path: '/safety' },
  { name: 'Help Center', path: '/help' },
];

const providerLinks = [
  { name: 'Become a Provider', path: '/provider/register' },
  { name: 'Provider Resources', path: '/provider-resources' },
  { name: 'Community', path: '/community' },
  { name: 'App for Providers', path: '/provider-app' },
];

const legalLinks = [
  { name: 'Terms of Use', path: '/terms' },
  { name: 'Privacy Policy', path: '/privacy' },
  { name: 'Cookies Policy', path: '/cookies' },
  { name: 'Legal', path: '/legal' },
  { name: 'Data Rights', path: '/data-rights' },
];

/* ── Brand social SVG icons ───────────────────────────────────────────────── */

const FacebookIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const XIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const InstagramIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
  </svg>
);

const LinkedInIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const socialLinks = [
  { name: 'Facebook', icon: FacebookIcon, url: '#', hover: 'hover:text-[#1877F2]' },
  { name: 'X', icon: XIcon, url: '#', hover: 'hover:text-gray-900' },
  { name: 'Instagram', icon: InstagramIcon, url: '#', hover: 'hover:text-[#E4405F]' },
  { name: 'LinkedIn', icon: LinkedInIcon, url: '#', hover: 'hover:text-[#0A66C2]' },
];

/* ── Reusable link column ─────────────────────────────────────────────────── */

const FooterColumn: React.FC<{ title: string; links: { name: string; path: string }[] }> = ({
  title,
  links,
}) => (
  <div>
    <h3 className="font-semibold text-gray-900 text-sm mb-4">{title}</h3>
    <ul className="space-y-2.5">
      {links.map((link) => (
        <li key={link.name}>
          <Link
            to={link.path}
            className="text-gray-500 hover:text-gray-900 transition-colors duration-200 text-sm"
          >
            {link.name}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

/* ── Footer ───────────────────────────────────────────────────────────────── */

const Footer: React.FC = () => {
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <footer className="mt-auto w-full bg-white border-t border-gray-200 text-gray-700">
      {/* Main grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-10">
          {/* Brand + tagline — spans full width on mobile, first column on lg */}
          <div className="col-span-2 sm:col-span-3 lg:col-span-1 mb-4 lg:mb-0">
            <Link to="/" className="inline-block mb-4" aria-label="Baytna Services home">
              <img
                src={Logo}
                alt="Baytna Services logo"
                className="h-10 w-auto object-contain select-none pointer-events-none"
              />
            </Link>
            <p className="text-sm text-gray-500 leading-relaxed max-w-xs mb-5">
              Connecting skilled professionals to homes in need — your trusted home&nbsp;services
              marketplace.
            </p>

            {/* Social links */}
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-gray-400 transition-colors duration-200 ${social.hover}`}
                    aria-label={`Follow us on ${social.name}`}
                  >
                    <Icon size={20} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Column 1 — Company */}
          <FooterColumn title="Company" links={companyLinks} />

          {/* Column 2 — Customers */}
          <FooterColumn title="Customers" links={customerLinks} />

          {/* Column 3 — Providers */}
          <FooterColumn title="Providers" links={providerLinks} />

          {/* Column 4 — Legal */}
          <FooterColumn title="Legal" links={legalLinks} />

          {/* Column 5 — Download App */}
          <div>
            <h3 className="font-semibold text-gray-900 text-sm mb-4">Get the App</h3>
            <p className="text-sm text-gray-500 mb-4 leading-relaxed">
              Book services anywhere, anytime.
            </p>

            <div className="flex flex-col gap-2.5">
              {/* App Store */}
              <a
                href="#"
                className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-200"
                aria-label="Download on the App Store"
              >
                <Smartphone size={18} />
                <span className="leading-tight">
                  <span className="block text-[10px] text-gray-400 uppercase tracking-wider">
                    Download on the
                  </span>
                  <span className="block text-sm font-semibold -mt-0.5">App Store</span>
                </span>
              </a>

              {/* Google Play */}
              <a
                href="#"
                className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-200"
                aria-label="Get it on Google Play"
              >
                <Smartphone size={18} />
                <span className="leading-tight">
                  <span className="block text-[10px] text-gray-400 uppercase tracking-wider">
                    Get it on
                  </span>
                  <span className="block text-sm font-semibold -mt-0.5">Google Play</span>
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom strip */}
      <div className="border-t border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-500">
            {/* Left — copyright + trust badge */}
            <div className="flex items-center gap-3 flex-wrap justify-center sm:justify-start">
              <span>&copy; {currentYear} Baytna Services. All rights reserved.</span>
              <span className="hidden sm:inline-flex items-center gap-1 text-gray-400">
                <Shield size={12} className="text-green-500" />
                Platform Guarantee
              </span>
            </div>

            {/* Right — scroll to top */}
            <button
              onClick={scrollToTop}
              className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-900 transition-colors duration-200 text-xs font-medium"
              aria-label="Back to top"
            >
              Back to Top
              <ArrowUp size={12} />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
